import type { AircraftSummary, FlightSegment } from "../api";
import type { TZ } from "../lib/time";
import { formatTime } from "../lib/time";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Separator } from "./ui/separator";
import { ChevronLeft, X } from "lucide-react";

interface Props {
  summary: AircraftSummary;
  segment: FlightSegment;
  onBack: () => void;
  onClose: () => void;
  tz: TZ;
}

function formatDuration(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return "<1m";
}

function StatCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-muted rounded px-2 py-1.5">
      <div className="text-[0.6rem] text-muted-foreground uppercase tracking-wide">{label}</div>
      <div className="text-xs text-foreground mt-0.5">{value}</div>
    </div>
  );
}

export default function FlightSummary({ summary, segment, onBack, onClose, tz }: Props) {
  const title = summary.callsigns[0] ?? summary.hex.toUpperCase();
  const altRange =
    summary.alt_min != null && summary.alt_max != null
      ? `${summary.alt_min.toLocaleString()}–${summary.alt_max.toLocaleString()} ft`
      : "—";

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2.5 bg-card/80 border-b border-border shrink-0">
        <span className="font-bold text-sm text-primary">{title}</span>
        <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-foreground" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-3 flex flex-col gap-3">
        {/* Aircraft info */}
        <div>
          <p className="text-[0.65rem] text-muted-foreground uppercase tracking-wide mb-1">Aircraft</p>
          <p className="text-sm font-semibold text-foreground">
            {summary.description ?? summary.type_code ?? "Unknown type"}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {[summary.registration, summary.owner_operator, summary.manufacture_year ? `Mfr ${summary.manufacture_year}` : null]
              .filter(Boolean)
              .join(" · ") || "No registry data"}
          </p>
          <div className="flex flex-wrap gap-1 mt-1.5">
            {summary.is_military && <Badge variant="destructive" className="text-[0.65rem] px-1.5 py-0">Military</Badge>}
            {summary.alert_ever   && <Badge className="text-[0.65rem] px-1.5 py-0 bg-orange-500 hover:bg-orange-500">Alert</Badge>}
            {summary.spi_ever     && <Badge className="text-[0.65rem] px-1.5 py-0 bg-orange-500 hover:bg-orange-500">SPI</Badge>}
            {summary.is_mlat      && <Badge variant="secondary" className="text-[0.65rem] px-1.5 py-0">MLAT</Badge>}
            {summary.is_tisb      && <Badge variant="secondary" className="text-[0.65rem] px-1.5 py-0">TIS-B</Badge>}
          </div>
        </div>

        <Separator />

        {/* Stats grid */}
        <div>
          <p className="text-[0.65rem] text-muted-foreground uppercase tracking-wide mb-1.5">Flight stats</p>
          <div className="grid grid-cols-2 gap-1">
            <StatCell label="Duration" value={formatDuration(segment.end_at - segment.start_at)} />
            <StatCell label="Positions" value={summary.position_count.toLocaleString()} />
            <StatCell label="Altitude" value={altRange} />
            <StatCell label="Max GS" value={summary.gs_max != null ? `${summary.gs_max} kt` : "—"} />
            <StatCell label="On ground" value={String(summary.on_ground_count)} />
          </div>
        </div>

        <Separator />

        {/* Time window */}
        <div>
          <p className="text-[0.65rem] text-muted-foreground uppercase tracking-wide mb-1">Time window</p>
          <p className="text-xs text-muted-foreground">
            {formatTime(segment.start_at, tz)}<br />
            → {formatTime(segment.end_at, tz)}
          </p>
        </div>

        {/* Callsigns */}
        {summary.callsigns.length > 0 && (
          <>
            <Separator />
            <div>
              <p className="text-[0.65rem] text-muted-foreground uppercase tracking-wide mb-1.5">Callsigns</p>
              <div className="flex flex-wrap gap-1">
                {summary.callsigns.map((c) => (
                  <Badge key={c} variant="outline" className="text-[0.65rem] px-1.5 py-0">{c}</Badge>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Squawks */}
        {summary.squawks.length > 0 && (
          <>
            <Separator />
            <div>
              <p className="text-[0.65rem] text-muted-foreground uppercase tracking-wide mb-1.5">Squawks</p>
              <div className="flex flex-wrap gap-1">
                {summary.squawks.map((s) => (
                  <Badge key={s} variant="secondary" className="text-[0.65rem] px-1.5 py-0">{s}</Badge>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Back button */}
        <Button
          variant="ghost"
          className="w-full justify-start text-xs h-8 mt-1"
          onClick={onBack}
        >
          <ChevronLeft className="h-3.5 w-3.5 mr-1" /> Back to flights
        </Button>
        <a
          href={`/flight/${summary.hex}/${segment.start_at}/${segment.end_at}`}
          className="block text-center text-xs text-primary hover:underline py-1"
        >
          View full details →
        </a>
      </div>
    </div>
  );
}
